import React, { useCallback, useEffect, useState } from "react";
import PaidMediaImpact from "./PaidMediaImpact";
import ModelInsights from "./ModelInsights";
import InvestToIncrease from "./InvestToIncrease";
import { RouteComponentProps } from "@reach/router";
import { Img, Button, ButtonType, CheckBox } from "../../Components";
import Overview from "./Overview";
import "./BrandImpact.scss";
import { useSetAreYouSure, useSetError } from "../../redux/modals";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework";
import { MetricsLambdaFetch } from "../../utils/fetch-utils";
import {
  BrandImpactData,
  IncrementalRoasTakeaways,
} from "@blisspointmedia/bpm-types/dist/BrandEquity";
import { formatModelOverviewOutcomeVariable } from "../BrandEquityUtils";

interface BrandImpactProps {
  company: string;
  groupByMetric: string;
  groupByLevel: string;
  groupByMetricOptions: Array<{
    label: string;
    value: string;
    s3Path: string;
    client_facing: boolean;
    kpi: string;
  }>;
  brandImpactData: BrandImpactData;
  incrementalRoasTakeAways: IncrementalRoasTakeaways;
  isInternalSelector: any;
  modelRunDate: string;
  setGroupByOptionsBrandImpact: any;
  setGroupByOptionsBrandImpactAll: any;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
}

const BrandImpact = ({
  company,
  groupByMetric,
  groupByLevel,
  brandImpactData,
  incrementalRoasTakeAways,
  groupByMetricOptions,
  isInternalSelector,
  modelRunDate,
  setGroupByOptionsBrandImpact,
  setGroupByOptionsBrandImpactAll,
  queryVolumeLongLabel,
  queryVolumeShortLabel,
}: BrandImpactProps & RouteComponentProps): JSX.Element => {
  const {
    weeklyBrandHealthTimeSeries,
    modelOverview,
    weeklyBrandHealthAreaChartSpend,
    implicationsData,
    paidMediaAreaChart,
    paidMediaBarChart,
    spendAndEffectBarChart,
    nextBestDollarBarChart,
    saturationData,
    decayData,
  } = brandImpactData;
  const setAreYouSure = useSetAreYouSure(true);
  const setError = useSetError();
  const [isQv, setGqv] = useState<boolean>(true);
  useEffect(() => {
    const checkKpi = groupByMetricOptions.some(item => item.kpi.includes("gqv"));
    setGqv(checkKpi);
  }, [groupByMetricOptions]);

  const toggleBrandEquityRun = useCallback(
    async (modelDate: string, checked: boolean, kpi: string) => {
      try {
        await MetricsLambdaFetch("/toggleBrandEquityRun", {
          method: "POST",
          body: {
            company,
            date: modelDate,
            client_facing: checked,
            kpi: kpi,
          },
        });
        const metric = kpi.charAt(0).toUpperCase() + kpi.slice(1).toLowerCase();
        setGroupByOptionsBrandImpact((prevOptions: any) =>
          prevOptions.map((item: any) => ({
            ...item,
            client_facing: item.label === metric ? !checked : checked,
          }))
        );
        setGroupByOptionsBrandImpactAll((prevOptions: any) => {
          const updatedOptions = { ...prevOptions };
          if (updatedOptions[modelDate]) {
            updatedOptions[modelDate] = updatedOptions[modelDate].map((item: any) =>
              item.label === metric ? { ...item, client_facing: !item.client_facing } : item
            );
          }
          return updatedOptions;
        });
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: reportError.message,
          reportError,
        });
      }
    },
    [company, setError, setGroupByOptionsBrandImpact, setGroupByOptionsBrandImpactAll]
  );

  return (
    <div>
      {!isQv && (
        <div className="brandImpactHeaderButtons">
          {groupByMetricOptions &&
            isInternalSelector &&
            !["tinuititest", "tinuitisearchtest"].includes(company) && (
              <Button
                className="archiveToggleButton"
                background="light"
                type={ButtonType.OUTLINED}
                variant={ButtonFrameworkVariant.TRAILING_ICON}
                onClick={() => {
                  const matchedItem = groupByMetricOptions.find(
                    item => item.value === groupByMetric
                  );
                  const visibility = matchedItem ? matchedItem.client_facing : false;
                  setAreYouSure({
                    title: visibility ? "Hide run from clients?" : "Show run to clients?",
                    message: `You're about to make this run ${
                      visibility ? "hidden from" : "visible to"
                    } clients. Are you sure you want to continue?`,
                    cancelText: "Cancel",
                    okayText: "Continue",
                    onOkay: () => {
                      toggleBrandEquityRun(
                        modelRunDate,
                        visibility,
                        groupByMetric.toLocaleLowerCase()
                      );
                    },
                  });
                }}
                icon={
                  <CheckBox
                    checked={
                      groupByMetricOptions.find(item => item.value === groupByMetric)
                        ?.client_facing ?? false
                    }
                    id="secondaryDesign"
                  />
                }
              >
                Client-Facing
              </Button>
            )}
        </div>
      )}
      <div className="brandEquityHeader">
        <div className="brandEquityTextHeader">
          <div className="brandEquityTextHeaderTitle">
            A strong brand drives business outcomes and creates a tailwind for performance marketing
          </div>
          <div className="brandEquityTextHeaderParagraph">
            Performance marketing generates results over days to weeks. Brand marketing operates
            over months to years, making unified assessment of the funnel challenging.
          </div>
          <div className="brandEquityTextHeaderParagraph">
            Bliss Point Brand Health quantifies brand equity's impact on the business for a holistic
            understanding of ROAS.
          </div>
        </div>
        <div className="brandEquityHeaderImageContainer">
          <Img
            src="https://cdn.blisspointmedia.com/assets/img/BusinessImpactDemo/BrandEquity_Infographic.png"
            alt="brand health header"
          />
        </div>
      </div>
      {Object.keys(modelOverview).length > 0 &&
        weeklyBrandHealthTimeSeries.length > 0 &&
        Object.keys(weeklyBrandHealthAreaChartSpend).length > 0 && (
          <Overview
            company={company}
            groupByMetric={groupByMetric}
            groupByLevel={groupByLevel}
            weeklyBrandHealthTimeSeries={weeklyBrandHealthTimeSeries}
            weeklyBrandHealthAreaChartSpend={weeklyBrandHealthAreaChartSpend}
            modelOverviewAll={modelOverview}
            incrementalRoasTakeaways={incrementalRoasTakeAways}
            isQv={isQv}
            queryVolumeLongLabel={queryVolumeLongLabel}
            queryVolumeShortLabel={queryVolumeShortLabel}
          />
        )}
      {Object.keys(implicationsData).length > 0 && (
        <PaidMediaImpact
          company={company}
          groupByMetric={formatModelOverviewOutcomeVariable(groupByMetric)}
          paidMediaImpactAreaChart={paidMediaAreaChart[groupByMetric]}
          paidMediaImpactBarChart={paidMediaBarChart}
          implicationsData={implicationsData}
          isQv={isQv}
          queryVolumeLongLabel={queryVolumeLongLabel}
          queryVolumeShortLabel={queryVolumeShortLabel}
        />
      )}
      {Object.keys(nextBestDollarBarChart).length > 0 &&
        Object.keys(spendAndEffectBarChart).length > 0 && (
          <InvestToIncrease
            company={company}
            groupByMetric={formatModelOverviewOutcomeVariable(groupByMetric)}
            unGroupedNextBestThousandDollars={nextBestDollarBarChart[groupByMetric]}
            unGroupedSpendAndEffect={spendAndEffectBarChart[groupByMetric]}
            groupByLevel={groupByLevel}
            queryVolumeLongLabel={queryVolumeLongLabel}
            queryVolumeShortLabel={queryVolumeShortLabel}
          />
        )}
      {Object.keys(decayData).length > 0 && Object.keys(saturationData).length > 0 && (
        <ModelInsights
          company={company}
          groupByMetric={groupByMetric}
          saturationDataUnformatted={saturationData[groupByMetric]}
          decayDataUnformmatted={decayData[groupByMetric]}
          groupByLevel={groupByLevel}
          queryVolumeShortLabel={queryVolumeShortLabel}
        />
      )}
    </div>
  );
};

export default BrandImpact;
